import React, { useState, useCallback, useEffect } from 'react';
import { SearchForm } from '../../components/searchForm';
import { TypeDropdown } from '../../components/radioButtons';
import PriceRangeSlider from '../../components/priceRangeSlider';
import DateRangeSlider from '../../components/dateRangeSlider';
import { useMapData } from './useMapdata';
import { MapPlot } from './MapPlot';
import SEOComponent from '../../components/SEOComponent';
import { useSubscription } from '../../components/RequireSubscription';
import { BuyButton } from '../../components/BuyButtons';
import { SignUpPrompt } from '../../components/SignUpPrompt';
import { MAPBOX_STYLES, COLOUR_SCALE_PALETTES } from './constants';
import { toSliderScale, fromSliderScale, toDateSliderScale, fromDateSliderScale, isPointInPolygon, aggregateDataByMonth } from './utils';
import ChartJSComponent from '../../components/ChartJSComponent';
import './Map.css';


async function fetchLocation(normalizedQuery) {
  const stub = normalizedQuery.match(/[a-zA-Z]+/g)[0];
  const url = `postcode/${stub}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const locationData = await response.json();
    const location = locationData[normalizedQuery];
    return location;
  } catch (error) {
    return null;
  }
}

function Map() {
  const { isSubscribed, userEmail, isAuthenticated } = useSubscription();
  const FREE_ZOOM_LIMIT = 10; // Free users can zoom up to level 10, subscribers get unlimited

  const [searchQuery, setSearchQuery] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [radioValue, setRadioValue] = useState('All');
  const [zoom, setZoom] = useState(6.5);
  const [center, setCenter] = useState({ lat: 51.748354, lon: -1.251952 });
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { isLoading, mapData, zoomLevel, colorBarRange, fetchMapData } = useMapData(searchQuery);
  const absoluteMaxPrice = mapData.length > 0 ? Math.max(...mapData.map(d => d.price)) * 1.2 : 50000000;
  const absoluteMinPrice = 3000;
  const currentDate = new Date();
  const stringMaxDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).toISOString().split('T')[0];

  const [minPrice, setMinPrice] = useState(absoluteMinPrice);
  const [maxPrice, setMaxPrice] = useState(absoluteMaxPrice);
  const [minDate, setMinDate] = useState('2020-01-01');
  const [maxDate, setMaxDate] = useState(stringMaxDate);
  const [filteredMapData, setFilteredMapData] = useState([]);
  const [mapboxStyleIndex, setMapboxStyleIndex] = useState(0);
  const [colourScaleIndex, setColourScaleIndex] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [chartData, setChartData] = useState(null);
  const currentMapboxStyle = MAPBOX_STYLES[mapboxStyleIndex];
  const currentColourScale = COLOUR_SCALE_PALETTES[colourScaleIndex];

  const changeMapboxStyle = () => {
    const newIndex = (mapboxStyleIndex + 1) % MAPBOX_STYLES.length;
    const newColourScaleIndex = (colourScaleIndex + 1) % COLOUR_SCALE_PALETTES.length;
    setMapboxStyleIndex(newIndex);
    setColourScaleIndex(newColourScaleIndex);
  };
  useEffect(() => {
    const minDateTimestamp = new Date(minDate).getTime();
    const maxDateTimestamp = new Date(maxDate).getTime();
    const filteredData = mapData.filter(item => 
      item.price >= minPrice && 
      item.price <= maxPrice && 
      item.date >= minDateTimestamp && 
      item.date <= maxDateTimestamp
    );
    setFilteredMapData(filteredData);
  }, [mapData, minPrice, maxPrice, minDate, maxDate]);
  const updateMapView = useCallback(({ zoom, center }) => {
    setZoom(zoom.toFixed(3));
    setCenter(center);
  }, []);


  const handleSearch = useCallback(async () => {
    setIsButtonDisabled(true);
    try {
      const normalizedQuery = searchQuery.replace(/\s+/g, "").toUpperCase();
      const location = await fetchLocation(normalizedQuery);

      if (location) {
        setMapKey((prevKey) => prevKey + 1);
        setCenter({ lat: location.latitude, lon: location.longitude });
        setZoom(14);
      } else {
        console.error('Location not found');
      }
    } catch (error) {
      console.error('Failed to search:', error);
    } finally {
      setIsButtonDisabled(false);
    }
  }, [searchQuery]);

  const handleRadioChange = useCallback((event) => {
    setRadioValue(event.target.value);
  }, []);

  useEffect(() => {
    let debounceTimer;

    if (!radioValue || !zoomLevel) {
      return;
    }

    debounceTimer = setTimeout(() => {
      fetchMapData(center.lon, center.lat, zoom, radioValue);
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [center, radioValue, zoom, zoomLevel, fetchMapData]);

  // Disable page scrolling when map component mounts, restore on unmount
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const handleMinPriceChange = (sliderValue) => {
    const newMinPrice = fromSliderScale(sliderValue, absoluteMinPrice, absoluteMaxPrice);
    setMinPrice(newMinPrice);
  };

  const handleMaxPriceChange = (sliderValue) => {
    const newMaxPrice = fromSliderScale(sliderValue, absoluteMinPrice, absoluteMaxPrice);
    setMaxPrice(newMaxPrice);
  };

  const handleMinDateChange = (sliderValue) => {
    const newMinDate = fromDateSliderScale(sliderValue, '2020-01-01', stringMaxDate);
    setMinDate(newMinDate);
  };

  const handleMaxDateChange = (sliderValue) => {
    const newMaxDate = fromDateSliderScale(sliderValue, '2020-01-01', stringMaxDate);
    setMaxDate(newMaxDate);
  };
  
  const toggleVisibility = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handlePolygonSelect = (polygonPoints) => {
    if (polygonPoints.length > 2) {
      const filteredData = filteredMapData.filter(item => 
        isPointInPolygon([item.longitude, item.latitude], polygonPoints)
      );
      
      if (filteredData.length > 0) {
        const aggregatedData = aggregateDataByMonth(filteredData);
        const lastDate = aggregatedData[aggregatedData.length - 1]?.date;
        const endOfLastMonth = lastDate ? new Date(new Date(lastDate).getFullYear(), new Date(lastDate).getMonth() + 1, 0).toISOString().split('T')[0] : lastDate;
        
        const metadata = {
          scale_factor: 1,
          subtitle: `Selected Region (${filteredData.length} properties) - Type: ${radioValue}`,
          y_min: Math.min(...aggregatedData.map(d => d.geometric_mean)),
          y_max: Math.max(...aggregatedData.map(d => d.geometric_mean)),
          x_max: endOfLastMonth,
          y1_title: 'Average Price (£)',
          y2_title: 'Property Count'
        };
        
        setChartData({ data: aggregatedData, metadata });
        setShowChart(true);
      }
      setIsSelecting(false);
    }
  };

  const closeChart = () => {
    setShowChart(false);
    setChartData(null);
  };

  const toggleSelection = () => {
    setIsSelecting(!isSelecting);
    if (!isSelecting) {
      setShowChart(false);
      setChartData(null);
    }
  };

  return (
    <>
      <SEOComponent 
        title="Interactive Property Map - England & Wales Land Registry Data | Otta Property" 
        description="Explore England and Wales property data on our interactive map. Filter by property type, price range, and dates to visualise Land Registry sales data and identify market trends across different areas." 
        url="https://ottaproperty.co.uk/map" 
        image="https://ottaproperty.co.uk/map.webp" 
      />
      
      <div className="parent-container">
        <div className="options-container">
          <button 
            onClick={toggleSelection}
            style={{
              position: 'absolute',
              top: '0px',
              right: '195px',
              padding: '10px 15px',
              backgroundColor: isSelecting ? '#ff6b6b' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              zIndex: 11,
              width: '130px',
              whiteSpace: 'nowrap'
            }}
          >
            {isSelecting ? 'Cancel' : 'Plot Region'}
          </button>
          <button className="toggle-button" onClick={toggleVisibility}>
            {isCollapsed ? 'Show Options' : 'Hide Options'}
          </button>
          <button className="map-style-button" onClick={changeMapboxStyle}>
            🎨
          </button>
          <div className={`search-container ${isCollapsed ? 'hidden' : 'visible'}`}>
            <SearchForm
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSearch={handleSearch}
              isButtonDisabled={isButtonDisabled}
            />
            <TypeDropdown radioValue={radioValue} onChange={handleRadioChange} />
            <PriceRangeSlider
              min_label={`£${(Math.round(minPrice / 10) * 10).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}
              max_label={`£${(Math.round(maxPrice / 10) * 10).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`}
              min={1}
              max={1000}
              value1={toSliderScale(minPrice, absoluteMinPrice, absoluteMaxPrice)}
              value2={toSliderScale(maxPrice, absoluteMinPrice, absoluteMaxPrice)}
              onChange1={(value) => handleMinPriceChange(value)}
              onChange2={(value) => handleMaxPriceChange(value)}
              mapData={mapData}
              setFilteredData={setFilteredMapData}
              isCollapsed={isCollapsed}
            />
            <DateRangeSlider
              min_label={minDate}
              max_label={maxDate}
              min={1}
              max={1000}
              value1={toDateSliderScale(minDate, '2020-01-01', stringMaxDate)}
              value2={toDateSliderScale(maxDate, '2020-01-01', stringMaxDate)}
              onChange1={(value) => handleMinDateChange(value)}
              onChange2={(value) => handleMaxDateChange(value)}
              mapData={mapData}
              setFilteredData={setFilteredMapData}
              isCollapsed={isCollapsed}
            />
          </div>
        </div>
        
        <div className="map-container">
          <MapPlot
            key={mapKey}
            mapData={filteredMapData}
            mapCenter={center}
            zoomLevel={zoom}
            colorBarRange={colorBarRange}
            updateMapView={updateMapView}
            mapboxStyle={currentMapboxStyle}
            colorscalePalette={currentColourScale}
            isSelecting={isSelecting}
            onPolygonSelect={handlePolygonSelect}
          />
          
          {isSelecting && (
            <div style={{
              position: 'absolute',
              bottom: '20vh',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,0.8)',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '8px',
              zIndex: 1000,
              fontSize: '13px'
            }}>
              {zoom >= 13 
                ? 'Click and drag on the map to select a region to plot.'
                : 'Click and drag on the map to select a region to plot. Note: At this zoom level, the data might be sparser in urban locations due to memory limits.'
              }
            </div>
          )}
        </div>
        
        {showChart && chartData && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(95vw, 600px)',
            backgroundColor: 'white',
            border: '2px solid #ccc',
            borderRadius: '12px',
            padding: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            zIndex: 1001
          }}>
            <div style={{ width: '100%', aspectRatio: '16/9', position: 'relative' }}>
              <button
                onClick={closeChart}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(255,255,255,0.8)',
                  border: '1px solid #ccc',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#666',
                  lineHeight: '1',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
              <ChartJSComponent
                data={chartData.data}
                metadata={chartData.metadata}
              />
            </div>
          </div>
        )}

        {/* Zoom-based paywall overlay */}
        {!isSubscribed && zoom > FREE_ZOOM_LIMIT && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '12px',
              maxWidth: '500px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
              {!isAuthenticated ? (
                // User is not logged in - prompt to sign up
                <>
                  <h2 style={{ marginBottom: '20px', color: '#333' }}>Sign Up for Detailed View</h2>
                  <p style={{ marginBottom: '20px', color: '#666', fontSize: '16px' }}>
                    Create a free account to get started. Access high-resolution, postcode-level property data with a subscription.
                  </p>
                  <SignUpPrompt message="" />
                </>
              ) : (
                // User is logged in but not subscribed - show subscription paywall
                <>
                  <h2 style={{ marginBottom: '20px', color: '#333' }}>Subscribe for Detailed View</h2>
                  <p style={{ marginBottom: '20px', color: '#666', fontSize: '16px' }}>
                    You've reached the free zoom limit. Subscribe to access high-resolution, postcode-level property data.
                  </p>
                  <BuyButton userEmail={userEmail} />
                </>
              )}
              <button
                onClick={() => setZoom(FREE_ZOOM_LIMIT)}
                style={{
                  marginTop: '15px',
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Back to Free View
              </button>
            </div>
          </div>
        )}
      </div>
    </>

);
}

export default Map;
